import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { PublicUrls } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { motifHeroPhotoSrc } from '@bridebook/web/lib/wedding-website/motif/hero-photo-src';
import { isPrimaryPhotoSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { compact } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { CSSProperties, MouseEventHandler, useCallback } from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import { useIsEditMode } from '../../data/data-provider-panel';
import { MotifOrnament } from './motif-section-hooks';
import { useWeddingBasics } from './use-wedding-basics';

/**
 * Motif hero / overview — couple names, date, venue line and the RSVP button, plus an optional hero image taken from
 * the first website photo. Renders the `bb-ww` BEM contract; all styling comes from the external theme CSS.
 *
 * v2 hooks: `--i: 0` (the hero is index 0 of the page); a leading `.bb-ww__ornament` slot; and the `.bb-ww__monogram`
 * (couple initials) — both hidden by default, themes opt in. See layout-motif/docs/bem-contract.md.
 */
export const MotifHero = () => {
  const { t } = useTranslation('weddingWebsite');
  const { website, mainSupplier } = useWeddingWebsiteData();
  const { names, initials, formattedDate } = useWeddingBasics();

  // venue line
  const formattedAddress = mainSupplier
    ? compact([mainSupplier.name, mainSupplier.address?.city, mainSupplier.address?.country]).join(
        ', ',
      )
    : undefined;

  // hero image = first website photo (fallback: text-only hero)
  const heroPhoto = website.sections.find(isPrimaryPhotoSection)?.data?.[0];
  const heroSrc = heroPhoto?.path ? motifHeroPhotoSrc(heroPhoto.path) : undefined;

  // rsvp
  const market = useMarket();
  const websiteId = website.id ?? '';
  const router = useRouter();
  const guestNameFromQuery = router.query.guest as string | undefined;
  const guestIdFromQuery = router.query.guestId as string | undefined;
  const dispatch = useDispatch();
  const editMode = useIsEditMode();
  const isPublished = website.privacy?.published;

  const handleRSVPClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      if (editMode || !isPublished) {
        if (!isPublished) e.preventDefault();
        return;
      }
      dispatch(
        interactedWithWeddingWebsiteAnalyticsButtonClick({
          section: 'rsvp',
          element: 'RSVP',
        }),
      );
    },
    [dispatch, editMode, isPublished],
  );

  return (
    <header
      className="bb-ww__section bb-ww__section--hero bb-ww__hero"
      style={{ '--i': 0 } as CSSProperties}>
      <MotifOrnament />
      <div className="bb-ww__monogram" aria-hidden="true">
        <span className="bb-ww__monogram-initial">{initials[0]}</span>
        <span className="bb-ww__monogram-amp">&</span>
        <span className="bb-ww__monogram-initial">{initials[1]}</span>
      </div>
      {heroSrc && (
        <div className="bb-ww__photo-frame">
          <img className="bb-ww__photo" src={heroSrc} alt={names} />
        </div>
      )}
      <div className="bb-ww__headline">
        <h1 className="bb-ww__title">{names}</h1>
        <p className="bb-ww__date">
          {formattedDate ?? t('previewMode.tile.overview.dateToBeAnnounced')}
        </p>
        <p className="bb-ww__location">
          {formattedAddress ?? t('previewMode.tile.overview.venueToBeAnnounced')}
        </p>
        {website.config.rsvpVisible && (
          <a
            className="bb-ww__button bb-ww__button--rsvp"
            aria-disabled={!isPublished}
            href={`/${market.prefix}${PublicUrls.for.rsvp(websiteId, {
              guest: guestNameFromQuery,
              guestId: guestIdFromQuery,
            })}`}
            target="_blank"
            rel="noreferrer"
            onClick={handleRSVPClick}>
            {t('previewMode.tile.overview.cta')}
          </a>
        )}
      </div>
    </header>
  );
};
